import { TaskResponse, UpdateTaskRequest } from "@9amhealth/openapi";
import { getAppointmentTypeFromAperoLink } from "@9amhealth/shared/src/lib/aperoSchedulerMapping";
import { Blac, Cubit } from "blac-next";
import { isAperoLink } from "lib/Urls";
import { NavigateFunction } from "react-router-dom";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import OnboardingBloc from "src/state/OnboardingBloc/OnboardingBloc";
import { KnownProgram } from "src/constants/programs";
import { tracker } from "src/state/state";
import TaskManagementBloc, {
  type TaskResponseKnown,
  TaskActionCall,
  TaskKey,
  TaskObserverEvent
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import type { TranslationKey } from "src/types/translationKey";
import {
  AppPopup,
  AppQueryPopupsController,
  ReservedQueryParameters
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import TaskIcons from "./TaskIcons";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";

/**
 * TaskListBloc and related types for managing and displaying user tasks in the 9amHealth User App.
 *
 * This module provides the Cubit-based state management for the Task List UI, including:
 * - Task configuration and mapping
 * - Task filtering, ordering, and display logic
 * - Task interaction handling (popups, navigation, external URLs)
 * - Integration with program/task state and error reporting
 *
 * @module TaskListBloc
 */

export const LEGACY_SCHEDULING_POPUP_TYPE = "scheduling";

//#region Types & Config
/**
 * Configuration for a specific task, including translation, icon, popup, and navigation details.
 */
export interface TaskConfig {
  title?: TranslationKey;
  translationKey?: TranslationKey;
  iconType?: keyof typeof TaskIcons;
  popup?: AppPopup;
  popupParams?: Record<string, string>;
  link?: string;
}

/**
 * List of statuses for tasks that should be visible in the UI.
 */
export const VISIBLE_TASK_STATUSES = [
  TaskResponse.status.AVAILABLE,
  TaskResponse.status.IN_PROGRESS
];

/**
 * List of statuses that represent terminal (completed/skipped) tasks.
 */
export const TERMINAL_STATUS = [
  TaskResponse.status.COMPLETED,
  TaskResponse.status.SKIPPED
];

/**
 * Base mapping of TaskKey to TaskConfig, defining default configuration for known tasks.
 */
export const taskConfigMapBase: Record<TaskKey, TaskConfig | null> = {
  [TaskKey.CONFIRM_MOST_RECENT_INTAKE]: null,
  [TaskKey.UPLOAD_PRESCRIPTION_LABEL]: null,
  [TaskKey.UPLOAD_PROOF_OF_WEIGHT]: null,
  [TaskKey.ACTIVATE_HEALTH_SYNC]: {
    translationKey: "prompt.activate_health_sync",
    iconType: "heart-beat",
    popup: AppPopup.healthSyncSetup
  },
  [TaskKey.CHECKIN_QUESTIONNAIRE]: {
    translationKey: "prompt.checkin_questionnaire",
    iconType: "form",
    popup: AppPopup.questionnaire,
    popupParams: {
      questionnaireId: "jreXfFTE",
      title: "prompt.checkin_questionnaire",
      stay: "false"
    }
  },
  [TaskKey.MEDICAL_INSURANCE]: {
    translationKey: "prompt.add_medical_insurance",
    iconType: "card-photo",
    popup: AppPopup.medicalInsurance
  },
  [TaskKey.PHARMACY_INSURANCE]: {
    translationKey: "prompt.add_pharmacy_insurance",
    iconType: "money-shield",
    popup: AppPopup.profileEnterPharmacyInsurance
  },
  [TaskKey.PREFERRED_PHARMACY]: {
    translationKey: "prompt.select_pharmacy",
    iconType: "bowl-of-hygiena",
    popup: AppPopup.profileSelectPharmacy
  },
  [TaskKey.SELECT_PCP]: {
    iconType: "stethoscope",
    popup: AppPopup.profileSelectPCP,
    popupParams: { pcpShowSkip: "true" }
  },
  [TaskKey.RECURRING_CHECKIN_QUESTIONNAIRE]: {
    translationKey: "prompt.checkin_questionnaire",
    iconType: "form",
    popup: AppPopup.questionnaire,
    popupParams: {
      questionnaireId: "hwvunLpQ",
      title: "prompt.checkin_questionnaire"
    }
  },
  [TaskKey.BEHAVIORAL_HEALTH]: {
    translationKey: "prompt.behavior_questionnaire",
    iconType: "form",
    popup: AppPopup.behavioralHealth
  },
  [TaskKey.SCHEDULE_GETLABS]: {
    translationKey: "prompt.schedule_getlabs",
    iconType: "home-labs"
  },
  [TaskKey.AMAZON_WEIGHT_JOURNEY_WEEK1]: {
    translationKey: "prompt.startWeightJourney",
    iconType: "arm",
    popup: AppPopup.questionnaire,
    popupParams: {
      title: "prompt.startWeightJourney"
    }
  },
  [TaskKey.UPLOAD_LABS]: null,
  [TaskKey.INITIAL_LAB_QUESTIONNAIRE]: null,
  [TaskKey.COMPLETE_INITIAL_LAB_ORDER]: null,
  [TaskKey.COMPLETE_RECURRING_LAB_ORDER]: null,
  [TaskKey.PRE_ONBOARDING_SYNC_VISIT]: null,
  [TaskKey.PRE_COMPLETE_ONBOARDING]: null,
  [TaskKey.COMPLETE_ONBOARDING]: null,
  [TaskKey.INITIAL_SYNC_VISIT]: null,
  [TaskKey.INITIAL_APPLICATION_DATE]: null,
  [TaskKey.RXDIET_MEAL_PLAN_SETUP]: {
    translationKey: "rxDiet.button.createFirstMealPlan",
    iconType: "clipboard-apple",
    popup: AppPopup.rxDietNutritionProgram
  },
  [TaskKey.LOG_WEIGHT]: {
    translationKey: "prompt.log_weight",
    iconType: "weight-scale",
    popup: AppPopup.rpmDailyReadings
  },
  [TaskKey.ONBOARDING_CONSOLIDATED]: null,
  [TaskKey.ONBOARDING_LABS_CONSOLIDATED]: null,
  [TaskKey.CHOOSE_NEXT_JOURNEY]: null,
  [TaskKey.PHYSICAL_ACTIVITY_JOURNEY]: null,
  [TaskKey.MINDSET_JOURNEY]: null,
  [TaskKey.NUTRITION_JOURNEY]: null,
  [TaskKey.COACHING_RATING]: null,
  [TaskKey.GLP1_REFILL_SURVEY]: null,
  [TaskKey.GLP1_REFILL_SURVEY_CONSOLIDATED]: null,
  [TaskKey.WAIST_MEASUREMENT]: null
};

/**
 * Additional data that may be attached to a task, typically from the API.
 */
export type TaskConfigAdditionalData = {
  appointmentUrl?: string;
  schedulingUrl?: string;
  scheduling_url?: string;
  questionnaireRef?: {
    id: string;
    type: "TYPEFORM";
  };
  iconType?: keyof typeof TaskIcons;
  icon?: keyof typeof TaskIcons;
  title?: string;
  popupType?: AppPopup;
  popup_url?: string;
  link?: string;
  medication_display_name?: string;
  medication_ndc?: string;
  medication_start_date?: string;
  dose_form?: string;
  photoUploadCanBeSkippedByUser?: boolean;
  actionCall?: TaskActionCall;
  coachingName?: string;
  coachingUserId?: string;
};

/**
 * A task object with its configuration and any additional data, used for display and interaction.
 */
export type TaskWithConfig = TaskResponseKnown & {
  config: TaskConfig | null;
  additionalData?: TaskConfigAdditionalData;
  id?: string;
};

/**
 * Handlers for different types of task click interactions.
 */
export type TaskClickHandlers = {
  onExternalUrl: (url: string) => Promise<void>;
  onNavigate: (path: string) => void;
};

/**
 * Configuration for a popup interaction triggered by a task.
 */
export type TaskPopupConfig = {
  type: AppPopup;
  params: Record<string, string> & ReservedQueryParameters;
  onClose?: () => void;
};

/**
 * Result of a task interaction, describing the type and associated data for the action.
 */
export type TaskInteractionResult = {
  type: "external_url" | "navigate" | "popup" | "callbackMethod";
  data: {
    url?: string;
    path?: string;
    popup?: TaskPopupConfig;
    taskId?: string;
    callback?: () => void;
  };
};

/**
 * State shape for the TaskListBloc Cubit.
 */
export type TaskListState = {
  loading: boolean;
  displayTasks: TaskWithConfig[];
};

/**
 * Props required to initialize and operate the TaskListBloc.
 */
export type TaskCompletionBehavior = "default" | "none" | AppPopup;

export type TaskListProps = {
  programs: KnownProgram[];
  filterVisibleTasks?: (task: TaskResponseKnown) => boolean;
  consolidatedTasks?: boolean;
  navigate: NavigateFunction;
  openExternalUrl: (url: string) => Promise<void>;
  setTerminalTasks?: (progress: number) => void;
  setTotalTasks?: (count: number) => void;
  onTaskCompletionBehavior?: TaskCompletionBehavior;
  descriptionBelowLabel?: boolean;
};
//#endregion

//#region TaskListBloc Class
/**
 * Cubit for managing the state and logic of the Task List UI.
 *
 * Handles loading, filtering, and ordering tasks, as well as user interactions such as popups, navigation.
 * Integrates with the global task management state and program state.
 */
export default class TaskListBloc extends Cubit<TaskListState, TaskListProps> {
  static isolated = true;
  /**
   * Current mapping of TaskKey to TaskConfig, may be patched at runtime.
   */
  taskConfigMap = taskConfigMapBase;
  props: TaskListProps | null;

  /**
   * Track previous onboarding tasks to detect status changes
   */
  private previousOnboardingTasks: Map<string, TaskResponse.status> = new Map();

  /**
   * Flag to indicate if an onboarding task recently changed to terminal status
   */
  private shouldReopenOnboardingPopup = false;

  constructor(props?: TaskListProps) {
    super({
      loading: false,
      displayTasks: []
    });
    this.props = props ?? null;
  }

  /**
   * Mount lifecycle - set up observers
   */
  addListenerForTaskChanges = (): void => {
    // Listen for task state changes from TaskManagementBloc
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    taskManagementState.addObserver(TaskObserverEvent.TASK_LIST_CHANGED, () => {
      // Reload tasks to apply consolidation when tasks change
      this.updateTasksInList();
    });
  };

  //#region Task Loading & State
  /**
   * Load tasks for the given programs, updating the loading state.
   */
  loadTasks = async (
    options: {
      /** The programs to load tasks for. */
      programs?: KnownProgram[];
      /** Whether to set the loading state. */
      setLoading?: boolean;
    } = {}
  ): Promise<void> => {
    const { programs = this.props?.programs ?? [], setLoading = false } =
      options;

    if (setLoading) {
      this.patch({
        loading: true,
        displayTasks: []
      });
    }

    await this.fetchProgramsTasks(programs);
    this.updateTasksInList();
  };

  /**
   * Update the list of tasks to display, applying filters and ordering.
   */
  updateTasksInList = (): void => {
    const displayTasks = this.getDisplayTasks();

    this.patch({
      loading: false,
      displayTasks
    });

    this.updateContext();

    // Detect onboarding task status changes
    this.detectOnboardingTaskStatusChanges();
  };

  /**
   * Update the context for terminal and total tasks, calling provided prop callbacks.
   */
  updateContext = () => {
    const { displayTasks } = this.state;
    const terminalTasks = displayTasks.filter((task) =>
      TERMINAL_STATUS.includes(task.status)
    );
    this.props?.setTerminalTasks?.(terminalTasks.length);
    this.props?.setTotalTasks?.(displayTasks.length);
  };
  //#endregion

  //#region Task Filtering & Ordering
  /**
   * Filter tasks using the provided or default filter function.
   * @returns True if the task should be visible.
   */
  filterVisibleTasks = (
    /** The task to filter. */
    task: TaskResponseKnown
  ) => {
    const { filterVisibleTasks = this.defaultFilter } = this.props ?? {};
    return filterVisibleTasks(task);
  };

  /**
   * Default filter for visible tasks (status-based and behavior-based).
   */
  defaultFilter = (task: TaskResponseKnown) => {
    const statusVisible = VISIBLE_TASK_STATUSES.includes(task.status);
    const taskConfig = this.getTaskConfig(task, taskConfigMapBase);

    if (!task.behavior.userCanSee) {
      return false;
    }

    // check if task has popup types defined
    const taskHasPopupTypes = task.uiElements.popupType !== "undefined";
    // do not show task if it has no popup types defined and no popup in config
    if (!taskHasPopupTypes && !taskConfig?.popup) {
      return false;
    }

    return statusVisible;
  };

  /**
   * Order tasks according to the order in the config map.
   * @returns Ordered array of tasks.
   */
  orderTasks = (
    /** The tasks to order. */
    tasks: TaskResponseKnown[],
    /** The config map to use for ordering. */
    taskConfigMap: Record<TaskKey, TaskConfig | null>
  ): TaskResponseKnown[] => {
    // first order by config
    const byConfig = tasks.sort((a, b) => {
      // use the order from TaskConfigMap
      const aIndex = Object.keys(taskConfigMap).indexOf(a.slug);
      const bIndex = Object.keys(taskConfigMap).indexOf(b.slug);
      return aIndex - bIndex;
    });

    // then order by group, the `consolidated` group should be first, the rest unchanged
    const byGroup = byConfig.sort((a, b) => {
      if (a.group === "consolidated" && b.group !== "consolidated") {
        return -1; // a comes first
      }
      if (b.group === "consolidated" && a.group !== "consolidated") {
        return 1; // b comes first
      }
      return 0; // keep original order for other groups
    });

    return byGroup;
  };
  //#endregion

  //#region Task Config & Processing
  /**
   * Get the configuration for a given task, searching by several key patterns.
   * @returns The found TaskConfig, or undefined.
   */
  getTaskConfig = (
    /** The task to look up. */
    task: TaskResponseKnown,
    /** The current config map. */
    taskConfigMap: Record<TaskKey, TaskConfig | null>
  ): TaskConfig | undefined => {
    const checksKeys = [
      `${task.program}.${task.group}.${task.slug}`,
      `${task.group}.${task.slug}`,
      task.slug
    ] as TaskKey[];

    let foundConfig: TaskConfig | undefined;
    for (const key of checksKeys) {
      const config = taskConfigMap[key];
      if (config) {
        foundConfig = config;
        break;
      }
    }
    return foundConfig;
  };

  /**
   * Patch the current task config map (used for dynamic config updates).
   */
  setTaskConfigMap = (
    /** The new config map. */
    taskConfigMap: Record<TaskKey, TaskConfig | null>
  ) => {
    this.taskConfigMap = taskConfigMap;
  };

  /**
   * Determine if a task config should be shown, based on onboarding state and task type.
   * @param task - The task to check.
   * @param onboardingTasksVisible - List of visible onboarding tasks.
   * @returns True if the task should be shown.
   */
  taskConfigShouldShow = (
    task: TaskResponseKnown,
    onboardingTasksVisible: TaskResponseKnown[]
  ): boolean => {
    const fullKey = `${task.program}.${task.group}.${task.slug}` as TaskKey;
    const userPreferences = Blac.getBloc(UserPreferencesCubit);

    const isVisitOnlyOnboarding = userPreferences.userHasVisitOnlyOnboarding;

    /**
     * only show meal plan setup if onboarding tasks (for visit only) are not visible (user has not completed visit first onboarding)
     */
    if (
      isVisitOnlyOnboarding &&
      fullKey.endsWith(TaskKey.RXDIET_MEAL_PLAN_SETUP)
    ) {
      return onboardingTasksVisible.length === 0;
    }

    return true;
  };

  /**
   * Process a list of tasks, attaching config and filtering for display.
   * @param tasks - The tasks to process.
   * @param taskConfigMap - The config map to use.
   * @param tasksVisible - List of currently visible tasks.
   * @returns Array of TaskWithConfig for display.
   */
  processTasksWithConfig = (
    tasks: TaskResponseKnown[],
    taskConfigMap: Record<TaskKey, TaskConfig | null>,
    tasksVisible: TaskResponseKnown[]
  ): TaskWithConfig[] => {
    return tasks
      .map((task): TaskWithConfig | null => {
        const config = this.getTaskConfig(task, taskConfigMap);

        if (!config) {
          return null;
        }

        if (!this.taskConfigShouldShow(task, tasksVisible)) {
          return null;
        }

        const taskWithConfig: TaskWithConfig = {
          ...task,
          additionalData: task.additionalData as
            | TaskConfigAdditionalData
            | undefined,
          config
        };

        const title =
          config.translationKey ??
          taskWithConfig.uiElements?.title ??
          taskWithConfig.additionalData?.title ??
          "";

        if (!title) {
          return null;
        }

        return taskWithConfig;
      })
      .filter((t): t is TaskWithConfig => t !== null);
  };
  //#endregion

  //#region Task Fetching
  /**
   * Load tasks for the given programs.
   */
  fetchProgramsTasks = async (
    /** The programs to load tasks for. */
    programs: KnownProgram[]
  ): Promise<void> => {
    try {
      const taskManagementState = Blac.getBloc(TaskManagementBloc);
      await Promise.all(
        programs.map((program) => taskManagementState.loadProgramTasks(program))
      );
    } catch (error) {
      if (error instanceof Error) {
        reportErrorSentry(error);
      }
    }
  };
  //#endregion

  //#region Task Display
  /**
   * Get the list of tasks to display, with configuration and deduplication.
   * @returns Promise resolving to an array of TaskWithConfig.
   */
  getDisplayTasks = (): TaskWithConfig[] => {
    const { consolidatedTasks, state } = Blac.getBloc(TaskManagementBloc);

    const userTasks = this.props?.consolidatedTasks
      ? consolidatedTasks
      : state.tasks;

    let programTasksList: TaskResponseKnown[] = userTasks.filter(
      this.filterVisibleTasks
    );

    // Deduplicate tasks with same path, keeping the one with latest updatedAt
    const uniqueTasksMap = new Map<string, TaskResponseKnown>();
    for (const task of programTasksList) {
      const taskPath = `${task.program}/${task.group}/${task.slug}`;
      const existingTask = uniqueTasksMap.get(taskPath);

      if (!existingTask) {
        uniqueTasksMap.set(taskPath, task);
      } else {
        const existingUpdatedAt = new Date(
          existingTask.updatedAt || "1970-01-01"
        );
        const currentUpdatedAt = new Date(task.updatedAt || "1970-01-01");

        if (currentUpdatedAt >= existingUpdatedAt) {
          uniqueTasksMap.set(taskPath, task);
        }
      }
    }
    programTasksList = Array.from(uniqueTasksMap.values());

    if (programTasksList.length !== 0) {
      // patch config
      programTasksList.forEach((task) => {
        const config = this.getTaskConfig(task, this.taskConfigMap);
        if (config) {
          return;
        }

        const additionalData = task.additionalData as TaskConfigAdditionalData;
        const { title, iconType, popupType } = additionalData;
        // only add popup params if title and icon are set from API
        if (title && iconType) {
          const popupParams: Record<string, string | undefined> = { title };

          // add questionnaireId if popupType is questionnaire
          if (popupType === AppPopup.questionnaire) {
            popupParams.questionnaireId = additionalData.questionnaireRef?.id;
          }

          // handle scheduling/iframe popups
          const isLegacyScheduling =
            (popupType as string) === LEGACY_SCHEDULING_POPUP_TYPE;
          const isIframePopup =
            popupType === AppPopup.iframe || isLegacyScheduling;
          const schedulingUrl =
            additionalData.popup_url ||
            additionalData.schedulingUrl ||
            additionalData.scheduling_url;

          // determine final popup type
          let finalPopupType: AppPopup | undefined = popupType;
          if (isIframePopup && schedulingUrl) {
            if (isAperoLink(schedulingUrl)) {
              // Apero links open in-app scheduler directly
              finalPopupType = AppPopup.scheduleAppointment;
              try {
                popupParams.type =
                  getAppointmentTypeFromAperoLink(schedulingUrl);
              } catch (error) {
                reportErrorSentry(error as Error);
              }
            } else {
              // non-Apero URLs open in iframe
              finalPopupType = AppPopup.iframe;
              popupParams.url = schedulingUrl;
            }
          }

          const fullKey =
            `${task.program}.${task.group}.${task.slug}` as TaskKey;
          this.taskConfigMap[fullKey] = {
            title,
            iconType,
            popup: finalPopupType,
            popupParams,
            link: additionalData.link
          } as TaskConfig;
          this.setTaskConfigMap({ ...this.taskConfigMap });
        }
      });
    }

    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    const tasksVisible = taskManagementState.getTaskByStatus(
      VISIBLE_TASK_STATUSES,
      KnownProgram.ONBOARDING
    );

    const orderedTasks = this.orderTasks(programTasksList, this.taskConfigMap);

    const resultTasks = this.processTasksWithConfig(
      orderedTasks,
      this.taskConfigMap,
      tasksVisible
    );

    return resultTasks;
  };

  //#endregion

  get allOnboardingTasksFinished(): boolean {
    return this.getDisplayTasks().every((task) =>
      TERMINAL_STATUS.includes(task.status)
    );
  }

  private detectOnboardingTaskStatusChanges(): void {
    if (!this.props?.programs.includes(KnownProgram.ONBOARDING)) {
      return;
    }

    const currentOnboardingTasks = this.getDisplayTasks().filter(
      (task) => task.program === KnownProgram.ONBOARDING
    );

    let hasTaskChangedToTerminal = false;

    for (const task of currentOnboardingTasks) {
      const taskKey = `${task.program}.${task.group}.${task.slug}`;
      const previousStatus = this.previousOnboardingTasks.get(taskKey);
      const currentStatus = task.status;

      if (
        previousStatus &&
        !TERMINAL_STATUS.includes(previousStatus) &&
        TERMINAL_STATUS.includes(currentStatus)
      ) {
        hasTaskChangedToTerminal = true;
      }

      this.previousOnboardingTasks.set(taskKey, currentStatus);
    }

    const currentTaskKeys = new Set(
      currentOnboardingTasks.map(
        (task) => `${task.program}.${task.group}.${task.slug}`
      )
    );
    for (const [key] of this.previousOnboardingTasks) {
      if (!currentTaskKeys.has(key)) {
        this.previousOnboardingTasks.delete(key);
      }
    }

    if (hasTaskChangedToTerminal) {
      const hasNonTerminalTasks = currentOnboardingTasks.some(
        (task) => !TERMINAL_STATUS.includes(task.status)
      );
      this.shouldReopenOnboardingPopup = hasNonTerminalTasks;
    }
  }

  /**
   * Handle a task click event.
   */
  handleTaskClick = (
    /** The task that was clicked. */
    task: TaskWithConfig
  ) => {
    const result = this.getTaskInteractionResult(task);

    if (!result) {
      reportErrorSentry(new Error(`Task ${task.slug} has no click handler`));
      return;
    }

    switch (result.type) {
      case "external_url":
        if (result.data.url) {
          void this.props?.openExternalUrl(result.data.url);
        }
        break;

      case "navigate":
        if (result.data.path) {
          this.props?.navigate(result.data.path);
        }
        break;

      case "popup":
        if (result.data.popup) {
          const isOnboardingTask = task.program === KnownProgram.ONBOARDING;

          AppQueryPopupsController.openPopup(result.data.popup.type, {
            additionalParameters: result.data.popup.params,
            onEvent: {
              popupClosed: () => {
                void this.loadTasks({
                  programs: this.props?.programs ?? []
                }).then(() => {
                  if (
                    isOnboardingTask &&
                    this.props?.programs.includes(KnownProgram.ONBOARDING) &&
                    this.shouldReopenOnboardingPopup
                  ) {
                    const behavior =
                      this.props?.onTaskCompletionBehavior ?? "default";

                    let popupToReopen: AppPopup | null = null;
                    if (behavior === "default") {
                      popupToReopen = AppPopup.onboardingTasks;
                    } else if (
                      behavior !== "none" &&
                      typeof behavior === "string"
                    ) {
                      popupToReopen = behavior;
                    }

                    if (popupToReopen) {
                      AppQueryPopupsController.openPopup(popupToReopen);
                    }

                    // Reset the flag after opening the popup
                    this.shouldReopenOnboardingPopup = false;
                  }
                });
              }
            }
          });
        }
        break;
      case "callbackMethod":
        if (result.data.callback) {
          result.data.callback();
        }
        break;
    }
  };

  /**
   * Determines the type of interaction for a given task (popup, navigation, external URL).
   * Delegates to specialized handlers for each interaction type.
   * @param task - The task to interact with.
   * @returns TaskInteractionResult or null if no interaction is defined.
   */
  getTaskInteractionResult = (
    /** The task to interact with. */
    task: TaskWithConfig
  ): TaskInteractionResult | null => {
    // Try each handler in order, only track if a valid result is returned

    const result =
      this.handleGetLabsScheduling(task) ??
      this.handleQuestionnaire(task) ??
      this.handleHardcodedPopups(task) ??
      this.handleGenericPopup(task) ??
      this.handleNavigationLinks(task) ??
      this.handleActionCall(task) ??
      null;

    if (result) {
      tracker.track("Task Item Clicked", {
        data: {
          "Task ID": task.id,
          "Task Slug": task.slug,
          "Task Group": task.group,
          "Task Program": task.program,
          "Task Status": task.status,
          "Task Title":
            task.config?.title ??
            translate(task.config?.translationKey ?? "unknown")
        }
      });
    }

    return result;
  };

  /**
   * Handles the GetLabs scheduling interaction for a task.
   * @param task - The task to check for GetLabs scheduling.
   * @returns TaskInteractionResult for external_url if applicable, otherwise null.
   */
  private handleGetLabsScheduling(
    task: TaskWithConfig
  ): TaskInteractionResult | null {
    if (task.slug === TaskKey.SCHEDULE_GETLABS) {
      const { appointmentUrl } = task.additionalData as {
        appointmentUrl?: string;
      };
      if (appointmentUrl) {
        return {
          type: "external_url",
          data: { url: appointmentUrl }
        };
      } else {
        reportErrorSentry(new Error(`Task ${task.slug} has no appointmentUrl`));
      }
    }
    return null;
  }

  /**
   * Handles questionnaire popup interaction for a task.
   * @param task - The task to check for questionnaire interaction.
   * @param taskConfigMap - The config map to use for popup params.
   * @returns TaskInteractionResult for popup if applicable, otherwise null.
   */
  private handleQuestionnaire(
    task: TaskWithConfig
  ): TaskInteractionResult | null {
    if (task.additionalData?.questionnaireRef?.id) {
      const config = this.getTaskConfig(task, this.taskConfigMap);
      const additionalParams = config?.popupParams ?? {};

      return {
        type: "popup",
        data: {
          popup: {
            type: AppPopup.questionnaire,
            params: {
              questionnaireId: task.additionalData.questionnaireRef.id,
              ...additionalParams
            }
          }
        }
      };
    }
    return null;
  }

  /**
   * Handles hardcoded popup interactions for a task (e.g., RXDIET_MEAL_PLAN_SETUP, PREFERRED_PHARMACY).
   * @param task - The task to check for hardcoded popup interaction.
   * @returns TaskInteractionResult for popup if applicable, otherwise null.
   */
  private handleHardcodedPopups(
    /** The task to check for hardcoded popup interaction. */
    task: TaskWithConfig
  ): TaskInteractionResult | null {
    if (task.config?.popup) {
      let customPopupParams: typeof task.config.popupParams | null = null;

      // meal plan setup popup
      if (
        `${task.program}.${task.group}.${task.slug}` ===
        TaskKey.RXDIET_MEAL_PLAN_SETUP
      ) {
        customPopupParams = {
          showFirstStep: VISIBLE_TASK_STATUSES.includes(task.status).toString()
        };
      }

      // preferred pharmacy popup
      if (task.slug === TaskKey.PREFERRED_PHARMACY) {
        customPopupParams = {
          showTaskSkip: "true"
        };
      }

      if (!customPopupParams) {
        return null;
      }

      return {
        type: "popup",
        data: {
          popup: {
            type: task.config.popup,
            params: {
              ...task.config.popupParams,
              ...customPopupParams
            }
          }
        }
      };
    }
    return null;
  }

  /**
   * Handles generic popup interactions for a task.
   * @param task - The task to check for generic popup interaction.
   * @returns TaskInteractionResult for popup if applicable, otherwise null.
   */
  private handleGenericPopup(
    /** The task to check for generic popup interaction. */
    task: TaskWithConfig
  ): TaskInteractionResult | null {
    const popupType = task.config?.popup;

    if (task.slug === TaskKey.INITIAL_SYNC_VISIT) {
      const onboardingBloc = Blac.getBloc(OnboardingBloc);
      if (onboardingBloc.checkFastPassAccess()) {
        return {
          type: "callbackMethod",
          data: {
            callback: () => onboardingBloc.handleFastPassPopup(task, false)
          }
        };
      }
    }

    const knownType = popupType && Object.values(AppPopup).includes(popupType);

    if (popupType && !knownType) {
      reportErrorSentry(new Error(`Unknown popup type: ${popupType}`));
      return null;
    }

    if (knownType) {
      const params = task.config?.popupParams ?? {};
      params.title = translate(params.title as TranslationKey);
      return {
        type: "popup",
        data: {
          popup: {
            type: popupType,
            params
          }
        }
      };
    }
    return null;
  }

  /**
   * Handles navigation link interaction for a task.
   * @param task - The task to check for navigation link.
   * @returns TaskInteractionResult for navigation if applicable, otherwise null.
   */
  private handleNavigationLinks(
    task: TaskWithConfig
  ): TaskInteractionResult | null {
    if (task.config?.link) {
      return {
        type: "navigate",
        data: { path: task.config.link }
      };
    }
    return null;
  }

  handleActionCall = (
    /** The task to check for action call. */
    task: TaskWithConfig
  ): TaskInteractionResult | null => {
    const actionCall = task.additionalData?.actionCall;
    switch (actionCall) {
      case TaskActionCall.onboardingBlocCtaActionStepGetYourLabsDone: {
        const onboardingBloc = Blac.getBloc(OnboardingBloc);
        return {
          type: "callbackMethod",
          data: {
            callback: onboardingBloc.ctaActionStepGetYourLabsDone
          }
        };
      }
    }
    return null;
  };

  /**
   * Get the translation key for a task, if available.
   * @returns The translation key or an empty string.
   */
  getTranslationKey = (
    /** The task to get the translation key for. */
    task: TaskWithConfig
  ): TranslationKey | "" => {
    const foundConfig = this.getTaskConfig(task, this.taskConfigMap);
    if (!foundConfig || !foundConfig.translationKey) {
      return "";
    }
    return foundConfig.translationKey;
  };

  /**
   * Get the description for a task, based on additional data or status.
   * @param task - The task to get the description for.
   * @returns The description string or an empty string.
   */
  getDescription = (task: TaskWithConfig): string => {
    if (task.additionalData?.completionMethod) {
      return translate(task.additionalData.completionMethod as TranslationKey, {
        context: `${task.program}.${task.group}`
      });
    }

    if (
      this.props?.descriptionBelowLabel &&
      task.status === TaskResponse.status.SKIPPED
    ) {
      return translate("program.task.skipped", {
        context: `${task.program}.${task.group}`
      });
    }

    return "";
  };
  //#endregion

  //#region Task Status Update
  /**
   * Update the status of a task (e.g., mark as skipped or completed).
   */
  updateTaskStatus = async (
    /** The task to update. */
    task: TaskWithConfig,
    /** The new status to set. */
    status: UpdateTaskRequest.status
  ): Promise<void> => {
    try {
      const taskManagementState = Blac.getBloc(TaskManagementBloc);
      await taskManagementState.updateTaskStatus(task, status);
    } catch (error) {
      if (error instanceof Error) {
        reportErrorSentry(error);
      }
      throw error;
    }
  };
  //#endregion
}
//#endregion
